/** @format */
import {
  ExternalLinkIcon,
  QuestionMarkCircleIcon,
  XIcon,
} from '@heroicons/react/outline'
import {EyeIcon} from '@heroicons/react/solid'
import * as Dialog from '@radix-ui/react-dialog'
import {type Row, type Table} from '@tanstack/react-table'
import yaml from 'json-to-pretty-yaml'
import React, {useEffect, useMemo, useRef} from 'react'

import {ExternalLink} from '@src/components/tailwind/ExternalLink'
import {
  OpenModalButton,
  useModalContext,
} from '@src/components/tailwind/ModalContext'
import * as Modal from '@src/components/tailwind/ModalV2'
import * as Tooltip from '@src/components/tailwind/Tooltip'

import {useFormApi} from '@src/contexts'
import type {FormDetailed} from '@src/types/Form'

import type {Submission, SubmissionStatus} from './SubmissionsContext'
import {useTableModel} from './TableModel'
import {useIsFileUploadArray} from './useIsFileUploadArray'
import {formatDateFull, getFileName, sanitizeFieldName} from './utils'

type DetailPanelProps = {
  container: HTMLElement | null
}

export function DetailPanel(props: DetailPanelProps) {
  const {container} = props
  const {table} = useTableModel()
  const content = useRef<HTMLDivElement>(null)
  const row = getExpandedRow(table)

  // Reset scroll on row change
  useEffect(() => {
    content.current?.scrollTo({top: 0})
  }, [row])

  return (
    <Dialog.Root modal={false} open={!!row}>
      <Dialog.Portal container={container}>
        <Dialog.Content
          aria-describedby={undefined}
          className="absolute inset-y-0 right-0 z-10 w-full overflow-y-auto border-s border-gray-100 bg-white p-4 animate-in slide-in-from-right data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right sm:w-[50%] sm:min-w-60"
          // Prevent autofocus on an interactive element in the panel when open
          onOpenAutoFocus={event => event.preventDefault()}
          ref={content}
        >
          {row && <DetailPanelContent row={row} />}

          <Dialog.Close
            className="absolute right-2 top-2 flex-none rounded-lg text-gray-400 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
            onClick={() => table.resetExpanded()}
          >
            <span className="sr-only">Close</span>
            <XIcon className="h-6 w-6" aria-hidden="true" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

type DetailPanelContentProps = {
  row: Row<Submission>
}

function DetailPanelContent(props: DetailPanelContentProps) {
  const {row} = props
  const {_date, _id, _status, ...fields} = row.original
  const {current: currentForm} = useFormApi()

  useDetailPanelKeyboardNavigation(row)

  return (
    <>
      <Dialog.Title className="sr-only">
        <span>Details of submission </span>
        <span>{_id}</span>
      </Dialog.Title>

      <div className="border-b border-gray-100 pb-4">
        <time className="text-gray-500" dateTime={_date}>
          {formatDateFull(_date, currentForm.timezone)}
        </time>
      </div>

      <ul className="flex flex-col gap-y-4 py-4">
        {Object.entries(fields).map(([field, value]) => {
          const key = sanitizeFieldName(field)
          return (
            <li key={key}>
              <p className="whitespace-nowrap text-xs font-medium text-gray-500">
                {key}
              </p>
              <Field value={value} />
            </li>
          )
        })}
      </ul>

      <Status value={_status} />
    </>
  )
}

function useDetailPanelKeyboardNavigation(row: Row<Submission>): void {
  const {table} = useTableModel()

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      switch (event.key) {
        case 'ArrowUp': {
          event.preventDefault() // Prevent scrolling
          const {rows} = table.getRowModel()
          const previous = rows[row.index - 1]
          if (previous) {
            table.setExpanded({[previous.id]: true})
          }
          break
        }
        case 'ArrowDown': {
          event.preventDefault() // Prevent scrolling
          const {rows} = table.getRowModel()
          const next = rows[row.index + 1]
          if (next) {
            table.setExpanded({[next.id]: true})
          }
          break
        }
        case 'Escape':
          table.resetExpanded()
          break
        // Space
        case ' ': {
          event.preventDefault()
          const handleSelect = row.getToggleSelectedHandler()
          handleSelect(event)
          break
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [row, table])
}

type StatusProps = {
  value: SubmissionStatus
}

function Status(props: StatusProps) {
  const {value} = props
  const {current: currentForm} = useFormApi()
  const entries = useMemo(
    () =>
      Object.entries(
        getStatusDetail(
          {
            plugins: currentForm.plugins,
            routing_rules: currentForm.routing_rules,
          },
          value,
        ),
      ),
    [currentForm.plugins, currentForm.routing_rules, value],
  )

  if (entries.length === 0) {
    return null
  }

  return (
    <div>
      <p className="whitespace-nowrap text-xs font-medium text-gray-500">
        _status
      </p>
      <ul>
        {entries.map(([k, v]) => (
          <li className="flex gap-x-2" key={k}>
            <span className="font-semibold">{k}: </span>
            <span>{v}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// Export for testing
function getStatusDetail(
  form: Pick<FormDetailed, 'plugins' | 'routing_rules'>,
  value: SubmissionStatus,
): Record<string, string> {
  const o: Record<string, string> = {}
  for (const [k, v] of Object.entries(value)) {
    if (k === '') {
      if (v.error) {
        o.error = v.error
      } else if (v.status) {
        o.submission = v.status
      }
      continue
    }

    // If the key is error, not rule or plugin, it means it's a whole submission error.
    if (k === 'error') {
      o.error = v?.toString() ?? 'Unknown error'
      continue
    }

    const status = v.message || getMostSignificantStatus(v)
    if (!status) {
      continue
    }

    if (k.includes('@')) {
      o[k] = status
      continue
    }

    const [kind, id] = k.split(':')
    switch (kind) {
      case 'plugin': {
        const plugin = form.plugins.find(p => p.id === id)
        if (plugin && plugin.kind !== 'email') {
          o[plugin.kind] = status
        }
        break
      }
      case 'rule': {
        for (const [i, r] of form.routing_rules.entries()) {
          if (r.id === id) {
            const label = `rule ${i}`
            o[label] = status
            break
          }
        }
        break
      }
    }
  }
  return o
}

const statusSignificance = [
  'open',
  'delivered',
  'processed',
  'dispatched',
  'pending',
  'processing',
  'retrying',
  'reprocessing',
]

function getMostSignificantStatus(
  status: SubmissionStatus[string],
): string | undefined {
  if (status.error) {
    if (!('status' in status) || status.status === 'processed')
      return status.error

    return status.status
  }

  /** As we introduced a new format for status, we need to keep this backward compatibility
  // Originally we had status as a boolean, so we check if the key is in the status object
  // and return the first one that matches in the correct order of significance.
  Currently, we keep only one status per action, so we can safely return the first match. **/
  return statusSignificance.find(key =>
    'status' in status ? key === status.status : key in status,
  )
}

type FieldProps = {
  value: unknown
}

function Field(props: FieldProps) {
  const {value} = props
  const isFileUploadArray = useIsFileUploadArray()

  if (isFileUploadArray(value)) {
    return (
      <ul>
        {value.map(f => (
          <li key={f}>
            <FileUpload fileUrl={f} />
          </li>
        ))}
      </ul>
    )
  }

  if (typeof value === 'string') {
    if (value === '<deleted>') {
      return <DeletedFile />
    }

    if (/^https?:\/\/[^\s]+/i.test(value)) {
      return (
        <ExternalLink
          className="flex items-center gap-x-1 hover:underline"
          href={value}
        >
          <span className="truncate">{value}</span>
          <ExternalLinkIcon className="h-5 w-5 shrink-0" />
        </ExternalLink>
      )
    }

    return <span className="whitespace-pre-line">{value}</span>
  }

  return (
    <p className="whitespace-pre pt-1 font-mono text-sm">
      {yaml.stringify(value).replace(/^\n+/, '')}
    </p>
  )
}

function DeletedFile() {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger className="flex cursor-help items-center gap-x-1 rounded transition-colors hover:text-gray-700 focus:outline-none focus-visible:ring-2">
        <span>A deleted file</span>
        <QuestionMarkCircleIcon className="h-4 w-4 shrink-0" />
      </Tooltip.Trigger>
      <Tooltip.Content className="z-50 max-w-64 overflow-hidden rounded-md bg-gray-900/90 px-3 py-1.5 text-xs text-white animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95">
        This file was deleted because you reached your account's file storage
        limit.
      </Tooltip.Content>
    </Tooltip.Root>
  )
}

type FileUploadProps = {
  fileUrl: string
}

function FileUpload(props: FileUploadProps) {
  const {fileUrl} = props
  const fileName = getFileName(fileUrl)
  const {closeCurrentModal, isOpen} = useModalContext()

  if (/\.(jpeg|jpg|jfif|gif|png)$/i.test(fileName)) {
    const modal = `file-modal-${fileName}`
    return (
      <>
        <OpenModalButton
          className="flex items-center gap-x-1 font-semibold text-primary transition-colors hover:text-secondary"
          modal={modal}
        >
          <span>{fileName}</span>
          <EyeIcon className="h-4 w-4 shrink-0" />
        </OpenModalButton>

        <Modal.Root isOpen={isOpen(modal)} onClose={closeCurrentModal}>
          <Modal.DefaultLayout>
            <Modal.Header title={fileName} />
            <Modal.Content>
              <img alt={fileName} className="mx-auto block" src={fileUrl} />
            </Modal.Content>
            <Modal.Footer>
              <div className="flex flex-row-reverse">
                <ExternalLink href={fileUrl} className="buttonSecondary">
                  Download
                </ExternalLink>
              </div>
            </Modal.Footer>
          </Modal.DefaultLayout>
        </Modal.Root>
      </>
    )
  }

  return (
    <ExternalLink
      className="flex items-center gap-x-1 font-semibold text-primary transition-colors hover:text-secondary"
      href={fileUrl}
    >
      <span>{fileName}</span>
      <ExternalLinkIcon className="h-5 w-5 shrink-0" />
    </ExternalLink>
  )
}

function getExpandedRow(table: Table<Submission>): Row<Submission> | undefined {
  const {rowsById} = table.getExpandedRowModel()
  const {expanded} = table.getState()
  // We allow expanding one row at a time (auto-collapse other rows).
  // So we grab the first item, which could be a `string` or `undefined`.
  const [id] = Object.keys(expanded)
  return rowsById[id]
}

export const __testingOnly__ = {
  getStatusDetail,
}
